import { createReducer } from '@reduxjs/toolkit'
import { parseQuery } from '@calendly/http-tools'

import { SET_CONSENT_RESOLVED_STATE } from 'actions/consent'

// Synchronously detect `hide_gdpr_banner` at module load so the very first render of
// consumers (SegmentScript, CalAnonymousId, useIsAnalyticsAllowed) sees the same all-
// granted state that the old `isCookiesAllowedSelector` produced via the
// `shouldHideOnetrustSelector` bypass. Previously this seed happened only inside
// `ConsentStateBridge`'s useEffect — i.e. one render late — which caused the Segment
// script tag to be skipped on the first cycle and (worse) caused `CalAnonymousId` to
// call read-only `getCalendlyAnonymousId` and fire its module-level `onInitialized`
// guard with the wrong consent state. Mirrors the URL-at-module-load pattern in
// `reducers/gdpr.js`.
//
// Important: the legacy `shouldHideOnetrustSelector` required BOTH
// `hide_gdpr_banner=true` AND a real embed (`embed_domain` + `embed_type`) before
// bypassing OneTrust. We mirror that gate here so a non-embedded URL that happens to
// carry `?hide_gdpr_banner=true` (e.g. a stray share link) does not silently grant
// every category — that would be a privacy regression vs. the old selector.
const {
  hide_gdpr_banner: hideGdprBanner,
  embed_domain: embedDomain,
  embed_type: embedType,
} = parseQuery(window.location.search)
const shouldSeedAllGranted = Boolean(hideGdprBanner && embedDomain && embedType)

const ALL_GRANTED = {
  necessary: 'granted',
  functional: 'granted',
  performance: 'granted',
  targeting: 'granted',
}

const DEFAULT_CATEGORIES = {
  necessary: 'granted',
  functional: 'denied',
  performance: 'denied',
  targeting: 'denied',
}

const initialState = shouldSeedAllGranted
  ? { categories: ALL_GRANTED, source: 'hide_gdpr_banner' }
  : { categories: DEFAULT_CATEGORIES, source: 'default' }

const consentReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(SET_CONSENT_RESOLVED_STATE, (state, action) => {
      state.categories = action.payload.categories
      state.source = action.payload.source
    })
})

export default consentReducer
